import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useApiClient } from "../api/useApiClient";
import { ApiError } from "../api/client";
import type { FacturaDeLote, FacturaParaPagar, LoteBanco, LotePago } from "../types/reviewItem";
import { LineasRow, LineasToggle } from "../components/LineasRow";
import { TablaToolbar } from "../components/TablaToolbar";
import { Paginador } from "../components/Paginador";
import { ThOrdenable } from "../components/ThOrdenable";
import { AvisoError } from "../components/AvisoError";
import { abrirEnNuevaPestana } from "../utils/abrirEnNuevaPestana";
import {
  formatMonto,
  formatFecha,
  formatFechaHora,
  formatFechaHoraUtc,
  formatNumeroCfe,
  tipoCfeLabel,
  totalesPorMoneda,
} from "../utils/formato";
import { useTablaBandeja } from "../hooks/useTablaBandeja";
import { useRoles } from "../auth/useRoles";
import { ROLE_CONTADURIA } from "../constants/roles";
import { LotesBancoPanel } from "../components/LotesBancoPanel";
import { leerArchivoComoBase64 } from "../utils/archivoAdjunto";
import {
  TABS_BANCO,
  TOPE_LOTES_CON_FILTRO,
  aplicarFiltroBanco,
  descripcionCruce,
  importarEnSecuencia,
  motivoDemasiadosArchivos,
  motivoRangoInvalido,
  textoConfirmarQuitarLote,
  textoPagoBanco,
  tituloResumenImportacion,
  type FiltroBanco,
  type ResultadoArchivoLote,
} from "../utils/pagosBanco";
import { LotesPagoPanel, VincularDesdeImportacion, type EstadoDetalleLote } from "../components/LotesPagoPanel";
import {
  CLASE_ESTADO_LOTE,
  ETIQUETA_ESTADO_LOTE,
  esEditable,
  filtrarPorLoteYVencimiento,
  filtroLoteDeValor,
  lotesDestino,
  mensajeErrorLote,
  motivoNoSePuedeAgrupar,
  opcionesFiltroLote,
  textoConfirmarCorregir,
  textoConfirmarEliminar,
  textoConfirmarMover,
  textoConfirmarQuitarFactura,
  textoConfirmarVincular,
  textoLoteCreado,
  textoResultadoVinculo,
  type CandidatoVinculo,
} from "../utils/lotesPago";

const COLUMNAS_BASE = 12;

/** Clave de una factura en los endpoints de lotes (nunca el `id` del snapshot). */
const filaDe = (f: { trans_id: number; line_id: number }) => ({ trans_id: f.trans_id, line_id: f.line_id });

export const SIN_DATO_NO_INGRESO = "Sin dato (no ingresó por la bandeja)";
export const SIN_DATO_NO_AUTORIZO = "Sin dato (no se autorizó por la bandeja)";
export const AUTORIZANTE_NO_DISPONIBLE = "No disponible";

/**
 * Quién autorizó el INGRESO A SAP en un paso de la bandeja. [NUEVO 20/10/2026, S-5]
 *
 * Nunca deduce nada: si la factura no pasó por la bandeja lo dice; si la
 * consulta de la auditoría falló, dice "No disponible" (no "sin dato", que
 * sería afirmar algo que no sabemos).
 */
export function textoAutorizante(
  item: FacturaParaPagar,
  paso: "contaduria" | "aprobador"
): { texto: string; fecha: string | null; conDato: boolean } {
  if (!item.review_item_id) return { texto: SIN_DATO_NO_INGRESO, fecha: null, conDato: false };
  if (!item.autorizante_disponible) return { texto: AUTORIZANTE_NO_DISPONIBLE, fecha: null, conDato: false };
  const nombre = paso === "contaduria" ? item.autorizo_contaduria_nombre : item.autorizo_aprobador_nombre;
  const fecha = paso === "contaduria" ? item.autorizo_contaduria_at : item.autorizo_aprobador_at;
  if (!nombre) return { texto: SIN_DATO_NO_AUTORIZO, fecha: null, conDato: false };
  return { texto: nombre, fecha, conDato: true };
}

/** Lo que muestra la columna "Autorizó ingreso a SAP": el paso del Aprobador,
 *  que es el que manda la factura a SAP. Contaduría se ve en la fila expandida. */
export function autorizanteDeColumna(item: FacturaParaPagar) {
  return textoAutorizante(item, "aprobador");
}

const AYUDA_AUTORIZANTE =
  "Quién autorizó el ingreso de la factura a SAP en la bandeja (paso del Aprobador). " +
  "No es la aprobación del pago.";

export type FiltroPago = "todas" | "habilitadas" | "no_habilitadas";
/** Días de vencimiento a incluir. La sincronización trae 30 días hacia adelante. */
export type FiltroVencimiento = "hoy" | "7" | "15" | "30";

const TABS_PAGO: Array<{ clave: FiltroPago; etiqueta: string }> = [
  { clave: "todas", etiqueta: "Todas" },
  { clave: "habilitadas", etiqueta: "Habilitadas para pagar" },
  { clave: "no_habilitadas", etiqueta: "No habilitadas" },
];

const TABS_VENCIMIENTO: Array<{ clave: FiltroVencimiento; etiqueta: string }> = [
  { clave: "hoy", etiqueta: "Vencidas hoy" },
  { clave: "7", etiqueta: "+ 7 días" },
  { clave: "15", etiqueta: "+ 15 días" },
  { clave: "30", etiqueta: "+ 30 días" },
];

/** Identidad estable de una fila: `id` se regenera en cada sincronización. */
export function claveDe(f: Pick<FacturaParaPagar, "trans_id" | "line_id">): string {
  return `${f.trans_id}-${f.line_id}`;
}

export function aplicarFiltroPago(items: FacturaParaPagar[], filtro: FiltroPago): FacturaParaPagar[] {
  if (filtro === "habilitadas") return items.filter((i) => i.pago_habilitado);
  if (filtro === "no_habilitadas") return items.filter((i) => !i.pago_habilitado);
  return items;
}

/**
 * Confirmación antes de quitar TODAS las habilitaciones de la bandeja.
 * [NUEVO 04/11/2026] Pedido de Control de pagos: para armar un pago nuevo había
 * que destildar una por una. Ojo: la marca es compartida (se guarda en la base),
 * así que el texto avisa que la deja sin marcar para todos, y muestra cuántas y
 * por cuánto para que no se confunda con "limpiar mi selección".
 */
export function textoConfirmarQuitarHabilitaciones(habilitadas: FacturaParaPagar[]): string {
  const totales = totalesPorMoneda(habilitadas.map((f) => ({ monto: f.saldo_vencido, moneda: f.moneda })))
    .map(({ moneda, total }) => `${moneda} ${formatMonto(total)}`)
    .join(" · ");
  return (
    `¿Quitar la habilitación de pago de las ${habilitadas.length} factura(s) habilitadas` +
    `${totales ? ` (${totales})` : ""}?\n\n` +
    "Quedan sin marcar para todos los usuarios, no solo en tu pantalla. No toca SAP ni los lotes del banco."
  );
}

/**
 * Filtra por antigüedad. `dias_vencido` lo calcula la vista en SQL: positivo
 * es "ya venció hace N días", negativo es "vence dentro de N días".
 *
 * "hoy" (>= 0) es el default a propósito: es lo que la bandeja mostraba antes
 * de que la sincronización empezara a traer 30 días hacia adelante. Abrir con
 * todo mezclado sería un retroceso — lo que todavía no venció se muestra solo
 * si se lo pide.
 */
export function aplicarFiltroVencimiento(
  items: FacturaParaPagar[],
  filtro: FiltroVencimiento
): FacturaParaPagar[] {
  const minimo = filtro === "hoy" ? 0 : -Number(filtro);
  return items.filter((i) => i.dias_vencido !== null && i.dias_vencido >= minimo);
}

/** Subtotal por moneda de cada proveedor, sobre TODO lo filtrado (no solo la
 *  página visible): si un proveedor queda partido entre dos páginas, el
 *  encabezado tiene que seguir mostrando lo que se le debe en total. */
export function subtotalesPorProveedor(
  items: FacturaParaPagar[]
): Map<string, Array<{ moneda: string; total: number }>> {
  const porProveedor = new Map<string, FacturaParaPagar[]>();
  for (const f of items) {
    const clave = f.card_code ?? "—";
    const actual = porProveedor.get(clave);
    if (actual) actual.push(f);
    else porProveedor.set(clave, [f]);
  }
  const salida = new Map<string, Array<{ moneda: string; total: number }>>();
  for (const [clave, filas] of porProveedor) {
    salida.set(clave, totalesPorMoneda(filas.map((f) => ({ monto: f.saldo_vencido, moneda: f.moneda }))));
  }
  return salida;
}

/**
 * Control de facturas para pagar.
 *
 * El origen es SAP (la consulta "Documentos vencidos pendientes de pago" del
 * Query Manager), sincronizado con 30 días de horizonte. La pantalla filtra
 * sobre lo ya cargado, así que cambiar de "vencidas hoy" a "+30 días" es
 * instantáneo y no vuelve a consultar SAP.
 *
 * Marcar "Pago habilitado" es OPTIMISTA: el click se ve al instante y el POST
 * viaja en segundo plano. Es el único lugar de la app que hace esto — el resto
 * recarga la lista completa tras cada acción. Se justifica porque acá el dato
 * es un booleano idempotente, no una transición de máquina de estados, y
 * recargar 1.700 filas por click haría que no se sienta como un Excel, que es
 * exactamente lo que se pidió.
 */
export function FacturasParaPagarPage() {
  const api = useApiClient();
  const esContaduria = useRoles().includes(ROLE_CONTADURIA);

  const [items, setItems] = useState<FacturaParaPagar[]>([]);
  const [expandidos, setExpandidos] = useState<Set<string>>(new Set());
  const [seleccionadas, setSeleccionadas] = useState<Set<string>>(new Set());
  const [guardando, setGuardando] = useState<Set<string>>(new Set());
  const [filtroPago, setFiltroPago] = useState<FiltroPago>("todas");
  const [filtroVenc, setFiltroVenc] = useState<FiltroVencimiento>("hoy");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [sincronizando, setSincronizando] = useState(false);

  // [NUEVO 03/11/2026] Lotes de pago del banco, detrás del flag `pagos_banco`.
  // Con el flag apagado la pantalla queda exactamente como antes.
  const [pagosBanco, setPagosBanco] = useState(false);
  const [lotes, setLotes] = useState<LoteBanco[]>([]);
  const [verLotes, setVerLotes] = useState(false);
  const [filtroBanco, setFiltroBanco] = useState<FiltroBanco>("todas");
  const [importando, setImportando] = useState(false);
  // [NUEVO 04/11/2026] Carga de varios PDFs, quitar un lote y filtro por fecha.
  const [progresoImportacion, setProgresoImportacion] = useState<string | null>(null);
  const [resumenImportacion, setResumenImportacion] = useState<ResultadoArchivoLote[] | null>(null);
  const [quitandoId, setQuitandoId] = useState<number | null>(null);
  const [lotesDesde, setLotesDesde] = useState("");
  const [lotesHasta, setLotesHasta] = useState("");
  // Filtro APLICADO (no lo que se está tipeando). En un ref para que recargar
  // después de importar/quitar respete el rango sin re-disparar efectos.
  const filtroLotes = useRef<{ desde?: string; hasta?: string } | null>(null);
  const [filtroLotesActivo, setFiltroLotesActivo] = useState(false);
  // Error de validación del rango: en línea, no en `error` — con la bandeja
  // vacía `error` reemplaza la página entera.
  const [errorRango, setErrorRango] = useState<string | null>(null);
  // Solo se aplica la respuesta del último pedido: una lenta no pisa a una nueva.
  const pedidoLotes = useRef(0);

  // [NUEVO 04/11/2026] Lotes de pago de la app, detrás del flag `lotes_pago`.
  // Con el flag apagado nada de esto se usa ni se pide al backend.
  const [lotesPagoOn, setLotesPagoOn] = useState(false);
  const [lotesPago, setLotesPago] = useState<LotePago[]>([]);
  const [errorLotesPago, setErrorLotesPago] = useState<string | null>(null);
  const [filtroLote, setFiltroLote] = useState("todos");
  const [detalleLote, setDetalleLote] = useState<EstadoDetalleLote | null>(null);
  const [accionLoteEnCurso, setAccionLoteEnCurso] = useState(false);
  const pedidoLotesPago = useRef(0);
  // Id del detalle abierto, para que una respuesta lenta no pise a otro detalle.
  const detalleLoteId = useRef<number | null>(null);

  const COLUMNAS = COLUMNAS_BASE + (pagosBanco ? 1 : 0) + (lotesPagoOn ? 1 : 0);
  const ocupadoLotes = importando || quitandoId !== null;

  const cargarLotes = useCallback(async () => {
    const pedido = ++pedidoLotes.current;
    try {
      const resultado = await api.listarLotesPagoBanco(filtroLotes.current ?? {});
      if (pedido === pedidoLotes.current) setLotes(resultado);
    } catch (err) {
      if (pedido !== pedidoLotes.current) return;
      setError(err instanceof Error ? err.message : "No se pudieron cargar los lotes del banco.");
    }
  }, [api]);

  /** [NUEVO 04/11/2026] Si falla, la bandeja se muestra igual: el aviso va en el panel. */
  const cargarLotesPago = useCallback(async () => {
    const pedido = ++pedidoLotesPago.current;
    try {
      const resultado = await api.listarLotesPago();
      if (pedido !== pedidoLotesPago.current) return;
      setLotesPago(resultado);
      setErrorLotesPago(null);
    } catch {
      if (pedido !== pedidoLotesPago.current) return;
      setErrorLotesPago("No se pudieron cargar los lotes de pago. La bandeja funciona igual.");
    }
  }, [api]);

  /**
   * Importar o quitar un PDF del banco también cambia lotes de pago (pasa a
   * pagado, o vuelve a abierto). Con el flag apagado devuelve [] y no pide nada.
   * Usa cargarDetalleLote (definida más abajo): solo se llama desde handlers, ya inicializada.
   */
  function recargasLotesPago(): Array<Promise<unknown>> {
    if (!lotesPagoOn) return [];
    const recargas: Array<Promise<unknown>> = [cargarLotesPago()];
    if (detalleLoteId.current !== null) recargas.push(cargarDetalleLote(detalleLoteId.current));
    return recargas;
  }

  useEffect(() => {
    let cancelado = false;
    api
      .obtenerFuncionalidades()
      .then((f) => {
        if (cancelado) return;
        if (f.pagos_banco) {
          setPagosBanco(true);
          cargarLotes();
        }
        // Corre en paralelo con la carga de facturas (otro efecto), nunca encadenado a ella.
        if (f.lotes_pago) {
          setLotesPagoOn(true);
          cargarLotesPago();
        }
      })
      // Ante cualquier error queda apagado: es una funcionalidad adicional.
      .catch(() => undefined);
    return () => {
      cancelado = true;
    };
  }, [api, cargarLotes, cargarLotesPago]);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await api.listarFacturasParaPagar());
      setSeleccionadas(new Set());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error desconocido al cargar la lista.");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const visibles = useMemo(() => {
    // [NUEVO 04/11/2026] Filtro "Lote": un lote puntual muestra todas sus facturas,
    // sin el filtro de vencimiento, para que el total coincida con el del lote.
    const porVencimiento = lotesPagoOn
      ? filtrarPorLoteYVencimiento(items, filtroLoteDeValor(filtroLote, lotesPago), (xs) =>
          aplicarFiltroVencimiento(xs, filtroVenc)
        )
      : aplicarFiltroVencimiento(items, filtroVenc);
    const base = aplicarFiltroPago(porVencimiento, filtroPago);
    return pagosBanco ? aplicarFiltroBanco(base, filtroBanco) : base;
  }, [items, filtroVenc, filtroPago, pagosBanco, filtroBanco, lotesPagoOn, filtroLote, lotesPago]);

  const cantidadesBanco = useMemo(() => {
    const base = aplicarFiltroPago(aplicarFiltroVencimiento(items, filtroVenc), filtroPago);
    const conteo = {} as Record<FiltroBanco, number>;
    for (const { clave } of TABS_BANCO) conteo[clave] = aplicarFiltroBanco(base, clave).length;
    return conteo;
  }, [items, filtroVenc, filtroPago]);

  // Una pasada por cambio de `items`/filtro, no una por tab en cada render.
  const cantidadesPago = useMemo(() => {
    const base = aplicarFiltroVencimiento(items, filtroVenc);
    const conteo = {} as Record<FiltroPago, number>;
    for (const { clave } of TABS_PAGO) conteo[clave] = aplicarFiltroPago(base, clave).length;
    return conteo;
  }, [items, filtroVenc]);

  const cantidadesVenc = useMemo(() => {
    const conteo = {} as Record<FiltroVencimiento, number>;
    for (const { clave } of TABS_VENCIMIENTO) conteo[clave] = aplicarFiltroVencimiento(items, clave).length;
    return conteo;
  }, [items]);

  const tabla = useTablaBandeja(visibles, {
    camposBusqueda: ["proveedor_nombre", "card_code", "numero_comprobante", "nro_documento"],
    // Por proveedor a propósito: es lo que hace que los grupos queden
    // contiguos. Si se ordena por otra columna los encabezados aparecen donde
    // cambia el proveedor, que sigue siendo correcto aunque haya más grupos.
    ordenInicial: { columna: "proveedor_nombre", direccion: "asc" },
    camposNumericos: ["saldo_vencido", "dias_vencido"],
    claveEstable: "id",
  });

  const paraTotales = useMemo(
    () => tabla.filtradas.map((f) => ({ monto: f.saldo_vencido, moneda: f.moneda })),
    [tabla.filtradas]
  );
  const subtotales = useMemo(() => subtotalesPorProveedor(tabla.filtradas), [tabla.filtradas]);

  function toggleExpandido(clave: string) {
    setExpandidos((prev) => {
      const next = new Set(prev);
      next.has(clave) ? next.delete(clave) : next.add(clave);
      return next;
    });
  }

  function toggleSeleccion(clave: string, marcada: boolean) {
    setSeleccionadas((prev) => {
      const next = new Set(prev);
      marcada ? next.add(clave) : next.delete(clave);
      return next;
    });
  }

  /** Aplica la marca en el estado local. Se usa para el cambio optimista y
   *  también para revertirlo si el backend lo rechaza. */
  const aplicarLocal = useCallback((claves: Set<string>, habilitado: boolean) => {
    setItems((prev) =>
      prev.map((f) => (claves.has(claveDe(f)) ? { ...f, pago_habilitado: habilitado } : f))
    );
  }, []);

  async function marcar(filas: FacturaParaPagar[], habilitado: boolean) {
    if (filas.length === 0) return;
    const claves = new Set(filas.map(claveDe));
    setAviso(null);
    setError(null);
    aplicarLocal(claves, habilitado);
    setGuardando((prev) => new Set([...prev, ...claves]));

    try {
      const r = await api.marcarPagoHabilitado(
        filas.map((f) => ({ trans_id: f.trans_id, line_id: f.line_id })),
        habilitado
      );
      if (r.no_encontradas.length > 0) {
        // Se sincronizó mientras la pantalla estaba abierta: se revierten SOLO
        // las que no entraron, no todo el lote.
        const perdidas = new Set(r.no_encontradas.map(claveDe));
        aplicarLocal(perdidas, !habilitado);
        setAviso(
          `${r.actualizadas} actualizada(s). ${r.no_encontradas.length} ya no está(n) en la lista ` +
            "(se actualizó desde SAP mientras tenías la pantalla abierta) — actualizá para verlas."
        );
      }
    } catch (err) {
      aplicarLocal(claves, !habilitado); // revertir todo el lote
      setError(err instanceof Error ? err.message : "No se pudo guardar la marca.");
    } finally {
      setGuardando((prev) => {
        const next = new Set(prev);
        for (const c of claves) next.delete(c);
        return next;
      });
    }
  }

  async function marcarSeleccionadas(habilitado: boolean) {
    await marcar(items.filter((f) => seleccionadas.has(claveDe(f))), habilitado);
    setSeleccionadas(new Set());
  }

  /** Quita TODAS las habilitaciones de la bandeja (no solo las filtradas o de
   *  esta página), con confirmación. */
  async function quitarTodasLasHabilitaciones() {
    const habilitadas = aplicarFiltroPago(items, "habilitadas");
    if (habilitadas.length === 0) return;
    if (!window.confirm(textoConfirmarQuitarHabilitaciones(habilitadas))) return;
    await marcar(habilitadas, false);
    setSeleccionadas(new Set());
  }

  async function sincronizar() {
    setSincronizando(true);
    setError(null);
    setAviso(null);
    try {
      const r = await api.sincronizarFacturasParaPagar();
      setAviso(`Actualizado desde SAP: ${r.filas_leidas} documentos con vencimiento hasta ${formatFecha(r.fecha_corte)}.`);
      await cargar();
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo actualizar desde SAP.");
    } finally {
      setSincronizando(false);
    }
  }

  /** [AMPLIADO 04/11/2026] Varios PDFs: uno por vez (cada uno consulta SAP
   *  en el backend), sin cortar si uno falla, y un resumen por archivo. */
  async function importarLotes(files: File[]) {
    const demasiados = motivoDemasiadosArchivos(files.length);
    if (demasiados) {
      setError(demasiados);
      return;
    }
    setImportando(true);
    setError(null);
    setAviso(null);
    setResumenImportacion(null);
    try {
      const resultados = await importarEnSecuencia(
        files,
        async (file) =>
          api.importarLotePagoBanco({ pdf_base64: await leerArchivoComoBase64(file), nombre_archivo: file.name }),
        (actual, total) => setProgresoImportacion(total > 1 ? `Importando ${actual} de ${total}...` : "Importando...")
      );
      setResumenImportacion(resultados);
      if (resultados.some((r) => r.resultado !== "error")) {
        setVerLotes(true);
        await Promise.all([cargar(), cargarLotes(), ...recargasLotesPago()]);
      }
    } finally {
      setImportando(false);
      setProgresoImportacion(null);
    }
  }

  async function quitarLote(lote: LoteBanco) {
    if (!window.confirm(textoConfirmarQuitarLote(lote))) return;
    setQuitandoId(lote.id);
    setError(null);
    setAviso(null);
    try {
      await api.quitarLotePagoBanco(lote.id);
      setAviso(`Se quitó el lote ${lote.id_transaccion} del ${formatFecha(lote.fecha)}.`);
      await Promise.all([cargar(), cargarLotes(), ...recargasLotesPago()]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo quitar el lote.");
    } finally {
      setQuitandoId(null);
    }
  }

  async function aplicarFiltroLotes() {
    const motivo = motivoRangoInvalido(lotesDesde, lotesHasta);
    setErrorRango(motivo);
    if (motivo) return;
    filtroLotes.current = { desde: lotesDesde || undefined, hasta: lotesHasta || undefined };
    setFiltroLotesActivo(true);
    await cargarLotes();
  }

  async function quitarFiltroLotes() {
    filtroLotes.current = null;
    setFiltroLotesActivo(false);
    setErrorRango(null);
    setLotesDesde("");
    setLotesHasta("");
    await cargarLotes();
  }

  async function abrirPdfLote(loteId: number) {
    try {
      abrirEnNuevaPestana(await api.obtenerPdfLoteBanco(loteId));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("No se pudo obtener el PDF del lote:", err);
      window.alert("No se pudo abrir el PDF del lote. Ver consola para el detalle.");
    }
  }

  async function abrirDocumento(reviewItemId: string, tipo: "xml" | "pdf") {
    try {
      abrirEnNuevaPestana(await api.obtenerBlobSasUrl(reviewItemId, tipo));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(`No se pudo obtener el ${tipo.toUpperCase()}:`, err);
      window.alert(`No se pudo abrir el ${tipo.toUpperCase()}. Ver consola para el detalle.`);
    }
  }

  // -- Lotes de pago [NUEVO 04/11/2026] — solo se llaman con `lotesPagoOn` ----

  const cargarDetalleLote = useCallback(
    async (id: number) => {
      try {
        const lote = await api.obtenerLotePago(id);
        if (detalleLoteId.current === id) setDetalleLote({ id, lote, error: null });
      } catch (err) {
        if (detalleLoteId.current !== id) return;
        const status = err instanceof ApiError ? err.status : null;
        setDetalleLote({
          id,
          lote: null,
          error: mensajeErrorLote(status, err instanceof Error ? err.message : "", "cargar el detalle del lote"),
        });
      }
    },
    [api]
  );

  function alternarDetalleLote(id: number) {
    if (detalleLoteId.current === id) {
      detalleLoteId.current = null;
      setDetalleLote(null);
      return;
    }
    detalleLoteId.current = id;
    setDetalleLote({ id, lote: null, error: null });
    cargarDetalleLote(id);
  }

  /**
   * Corre una acción sobre lotes y, haya funcionado o no, actualiza lo que la
   * pantalla muestra (mismo criterio que useAccion): un 409 significa que el
   * lote cambió, y el texto del backend ya explica qué pasó. La bandeja se
   * actualiza sin la pantalla de "Cargando...", para no perder la posición.
   */
  async function accionLote<T>(
    descripcion: string,
    accion: () => Promise<T>,
    { limpiarSeleccion = false, banco = false }: { limpiarSeleccion?: boolean; banco?: boolean } = {}
  ): Promise<T | undefined> {
    setAccionLoteEnCurso(true);
    setError(null);
    setAviso(null);
    let resultado: T | undefined;
    try {
      resultado = await accion();
    } catch (err) {
      const status = err instanceof ApiError ? err.status : null;
      setError(mensajeErrorLote(status, err instanceof Error ? err.message : "", descripcion));
    } finally {
      const recargas: Array<Promise<unknown>> = [
        cargarLotesPago(),
        api
          .listarFacturasParaPagar()
          .then((nuevas) => {
            setItems(nuevas);
            // Solo si salió bien: ante un 409 la persona conserva su selección.
            if (limpiarSeleccion && resultado !== undefined) setSeleccionadas(new Set());
          })
          .catch(() =>
            setError((prev) => prev ?? "La acción se hizo, pero no se pudo actualizar la bandeja. Recargá la página.")
          ),
      ];
      if (banco && pagosBanco) recargas.push(cargarLotes());
      if (detalleLoteId.current !== null) recargas.push(cargarDetalleLote(detalleLoteId.current));
      await Promise.all(recargas);
      setAccionLoteEnCurso(false);
    }
    return resultado;
  }

  const seleccionActual = () => items.filter((f) => seleccionadas.has(claveDe(f)));

  async function crearLote() {
    const sel = seleccionActual();
    const motivo = motivoNoSePuedeAgrupar(sel);
    if (motivo) {
      setAviso(motivo);
      return;
    }
    const r = await accionLote("crear el lote", () => api.crearLotePago(sel.map(filaDe)), { limpiarSeleccion: true });
    if (r) setAviso(textoLoteCreado(r.nombre, sel));
  }

  async function agregarALote(loteId: number) {
    const lote = lotesPago.find((l) => l.id === loteId);
    if (!lote) return;
    const sel = seleccionActual();
    const motivo = motivoNoSePuedeAgrupar(sel, lote.moneda);
    if (motivo) {
      setAviso(motivo);
      return;
    }
    const r = await accionLote("agregar las facturas al lote", () => api.agregarFacturasLote(lote.id, sel.map(filaDe)), {
      limpiarSeleccion: true,
    });
    if (r) setAviso(`Se agregaron ${sel.length} factura(s) a ${lote.nombre}.`);
  }

  /** Devuelve si quedó vinculado, para que el panel limpie su selección solo en ese caso. */
  async function vincularLote(lote: LotePago, loteBanco: LoteBanco): Promise<boolean> {
    if (!window.confirm(textoConfirmarVincular(lote, loteBanco))) return false;
    const r = await accionLote("vincular el PDF del banco", () => api.vincularLotePago(lote.id, loteBanco.id), {
      banco: true,
    });
    if (!r) return false;
    setAviso(textoResultadoVinculo(lote.nombre, r));
    return true;
  }

  /** Desde el resumen de importación: el clic en "Vincular" es la confirmación. */
  async function vincularDesdeImportacion(indice: number, loteBancoId: number, candidato: CandidatoVinculo) {
    const r = await accionLote("vincular el PDF del banco", () => api.vincularLotePago(candidato.id, loteBancoId), {
      banco: true,
    });
    if (!r) return;
    setAviso(textoResultadoVinculo(candidato.nombre, r));
    setResumenImportacion((prev) =>
      prev
        ? prev.map((x, i) =>
            i === indice && x.vinculo
              ? { ...x, vinculo: { ...x.vinculo, vinculado: { id: candidato.id, nombre: candidato.nombre, estado: r.estado } } }
              : x
          )
        : prev
    );
  }

  async function corregirLote(lote: LotePago) {
    if (!window.confirm(textoConfirmarCorregir(lote))) return;
    const r = await accionLote("corregir el lote", () => api.corregirLotePago(lote.id), { banco: true });
    if (r) setAviso(`${lote.nombre} quedó abierto: ya podés agregar, sacar o mover facturas y volver a vincularlo.`);
  }

  async function eliminarLote(lote: LotePago) {
    if (!window.confirm(textoConfirmarEliminar(lote))) return;
    const r = await accionLote("eliminar el lote", async () => {
      const resultado = await api.eliminarLotePago(lote.id);
      // Recién con el borrado confirmado se cierra su detalle (si estaba abierto),
      // antes de la recarga: si no, daría "no encontrado".
      if (detalleLoteId.current === lote.id) {
        detalleLoteId.current = null;
        setDetalleLote(null);
      }
      return resultado;
    });
    if (r) setAviso(`Se eliminó ${lote.nombre}. Sus facturas quedaron libres.`);
  }

  async function quitarFacturaDeLote(lote: LotePago, f: FacturaDeLote) {
    if (!window.confirm(textoConfirmarQuitarFactura(f, lote))) return;
    const r = await accionLote("sacar la factura del lote", () => api.quitarFacturasLote(lote.id, [filaDe(f)]));
    if (r) setAviso(`Se sacó la factura ${f.numero_comprobante ?? ""} de ${lote.nombre}.`);
  }

  async function moverFacturaDeLote(lote: LotePago, f: FacturaDeLote, destino: LotePago) {
    if (!window.confirm(textoConfirmarMover(f, lote, destino))) return;
    const r = await accionLote("mover la factura", () => api.moverFacturasLote(lote.id, [filaDe(f)], destino.id));
    if (r) setAviso(`Se movió la factura ${f.numero_comprobante ?? ""} a ${destino.nombre}.`);
  }

  const filasPaginaSeleccionadas =
    tabla.filas.length > 0 && tabla.filas.every((f) => seleccionadas.has(claveDe(f)));

  // "Agregar a lote ▾": lotes editables en la moneda de la primera seleccionada.
  // Si la selección mezcla monedas, motivoNoSePuedeAgrupar lo avisa al elegir.
  const hayLotesEditables = lotesPago.some((l) => esEditable(l.estado));
  const monedaSeleccion = seleccionadas.size > 0 ? items.find((f) => seleccionadas.has(claveDe(f)))?.moneda ?? null : null;
  const destinosSeleccion = monedaSeleccion ? lotesDestino(lotesPago, monedaSeleccion) : [];
  const valorFiltroLote = (() => {
    const f = filtroLoteDeValor(filtroLote, lotesPago);
    return typeof f === "number" ? String(f) : f;
  })();

  if (loading) return <p>Cargando...</p>;
  if (error && items.length === 0) return <p className="error-text">Error: {error}</p>;

  return (
    <div>
      <h1>Control de facturas para pagar</h1>
      <p className="subtitle">
        Documentos vencidos pendientes de pago, consultados directamente de SAP. Marcá con el tilde lo
        que se habilita a pagar — se guarda solo.
      </p>

      <AvisoError mensaje={error} onCerrar={() => setError(null)} />
      {aviso && (
        <div className="aviso-info">
          {aviso}
          <button className="btn-ghost" onClick={() => setAviso(null)}>
            Cerrar
          </button>
        </div>
      )}

      <div className="tabla-toolbar">
        <div className="filtro-tabs" role="group" aria-label="Filtrar por vencimiento">
          {TABS_VENCIMIENTO.map(({ clave, etiqueta }) => (
            <button
              key={clave}
              type="button"
              aria-pressed={filtroVenc === clave}
              className={`filtro-tab${filtroVenc === clave ? " filtro-tab-activo" : ""}`}
              onClick={() => setFiltroVenc(clave)}
            >
              {etiqueta} <span className="filtro-tab-cantidad">{cantidadesVenc[clave]}</span>
            </button>
          ))}
        </div>

        {esContaduria && (
          <button className="btn-secundario" disabled={sincronizando} onClick={sincronizar}>
            {sincronizando ? "Actualizando..." : "Actualizar desde SAP"}
          </button>
        )}
      </div>

      <div className="tabla-toolbar">
        <div className="filtro-tabs" role="group" aria-label="Filtrar por pago habilitado">
          {TABS_PAGO.map(({ clave, etiqueta }) => (
            <button
              key={clave}
              type="button"
              aria-pressed={filtroPago === clave}
              className={`filtro-tab${filtroPago === clave ? " filtro-tab-activo" : ""}`}
              onClick={() => setFiltroPago(clave)}
            >
              {etiqueta} <span className="filtro-tab-cantidad">{cantidadesPago[clave]}</span>
            </button>
          ))}
        </div>

        {items.some((f) => f.pago_habilitado) && (
          <button
            className="btn-ghost"
            disabled={guardando.size > 0}
            title="Destilda todas las facturas habilitadas para pagar, para armar un pago nuevo"
            onClick={quitarTodasLasHabilitaciones}
          >
            ✕ Quitar todas las habilitaciones
          </button>
        )}

        {lotesPagoOn && (
          <label className="filtro-lote">
            Lote{" "}
            <select value={valorFiltroLote} onChange={(e) => setFiltroLote(e.target.value)}>
              {opcionesFiltroLote(lotesPago).map((o) => (
                <option key={o.valor} value={o.valor}>
                  {o.etiqueta}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {pagosBanco && (
        <div className="tabla-toolbar">
          <div className="filtro-tabs" role="group" aria-label="Filtrar por pago en el banco">
            {TABS_BANCO.map(({ clave, etiqueta, ayuda }) => (
              <button
                key={clave}
                type="button"
                title={ayuda}
                aria-pressed={filtroBanco === clave}
                className={`filtro-tab${filtroBanco === clave ? " filtro-tab-activo" : ""}`}
                onClick={() => setFiltroBanco(clave)}
              >
                {etiqueta} <span className="filtro-tab-cantidad">{cantidadesBanco[clave]}</span>
              </button>
            ))}
          </div>
          <div className="acciones-banco">
            <button className="btn-ghost" aria-expanded={verLotes} onClick={() => setVerLotes((v) => !v)}>
              {verLotes ? "Ocultar lotes del banco" : `Ver lotes del banco (${lotes.length})`}
            </button>
            {esContaduria && (
              <label className={`btn-secundario boton-archivo${importando ? " deshabilitado" : ""}`}>
                {importando ? progresoImportacion ?? "Importando..." : "Importar lotes del banco"}
                <input
                  type="file"
                  accept="application/pdf,.pdf"
                  multiple
                  disabled={importando}
                  onChange={(e) => {
                    const files = Array.from(e.target.files ?? []);
                    e.target.value = ""; // permite volver a elegir los mismos archivos
                    if (files.length > 0) importarLotes(files);
                  }}
                />
              </label>
            )}
          </div>
        </div>
      )}

      {resumenImportacion && (
        <div
          className={`aviso-info resumen-importacion${
            resumenImportacion.some((r) => r.resultado === "error") ? " con-errores" : ""
          }`}
        >
          <div>
            <b>{tituloResumenImportacion(resumenImportacion)}</b>
            <ul>
              {resumenImportacion.map((r, i) => (
                <li key={i} className={`resultado-${r.resultado}`}>
                  <span className="mono">{r.archivo}</span>: {r.mensaje}
                  {lotesPagoOn && r.vinculo ? (
                    <>
                      {" "}
                      <VincularDesdeImportacion
                        vinculo={r.vinculo}
                        monedaDe={(id) => lotesPago.find((l) => l.id === id)?.moneda}
                        puedeVincular={esContaduria}
                        ocupado={accionLoteEnCurso}
                        onVincular={(c) => vincularDesdeImportacion(i, r.vinculo!.loteBancoId, c)}
                      />
                    </>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
          <button className="btn-ghost" onClick={() => setResumenImportacion(null)}>
            Cerrar
          </button>
        </div>
      )}

      {lotesPagoOn && (
        <LotesPagoPanel
          lotes={lotesPago}
          error={errorLotesPago}
          onReintentar={cargarLotesPago}
          esContaduria={esContaduria}
          lotesBanco={lotes}
          ocupado={accionLoteEnCurso}
          detalle={detalleLote}
          onVerDetalle={alternarDetalleLote}
          onVincular={vincularLote}
          onCorregir={corregirLote}
          onEliminar={eliminarLote}
          onQuitarFactura={quitarFacturaDeLote}
          onMoverFactura={moverFacturaDeLote}
        />
      )}

      {pagosBanco && verLotes && (
        <section className="panel-lotes-banco">
          <h2>Lotes de pago del banco</h2>
          <p className="subtitle">
            Se importan antes de registrar el pago en SAP. Una vez registrado, las facturas pagadas salen de esta
            bandeja.
          </p>
          <div className="filtro-lotes-banco" role="group" aria-label="Filtrar lotes por fecha">
            <label>
              Desde <input type="date" value={lotesDesde} onChange={(e) => setLotesDesde(e.target.value)} />
            </label>
            <label>
              Hasta <input type="date" value={lotesHasta} onChange={(e) => setLotesHasta(e.target.value)} />
            </label>
            <button className="btn-secundario" disabled={ocupadoLotes} onClick={aplicarFiltroLotes}>
              Buscar
            </button>
            {filtroLotesActivo && (
              <button className="btn-ghost" disabled={ocupadoLotes} onClick={quitarFiltroLotes}>
                Ver los últimos 30
              </button>
            )}
            {errorRango && <span className="error-text">{errorRango}</span>}
            <span className="sin-resolver">
              {filtroLotesActivo
                ? lotes.length >= TOPE_LOTES_CON_FILTRO
                  ? `Se muestran los ${TOPE_LOTES_CON_FILTRO} más recientes del rango: acotá las fechas para ver el resto.`
                  : `${lotes.length} lote(s) en el rango.`
                : "Se muestran los últimos 30. Filtrá por fecha para ver anteriores."}
            </span>
          </div>
          <LotesBancoPanel
            lotes={lotes}
            onAbrirPdf={esContaduria ? abrirPdfLote : undefined}
            onQuitar={esContaduria ? quitarLote : undefined}
            quitandoId={quitandoId}
            quitarDeshabilitado={ocupadoLotes}
            mostrarPagadoSinAprobacion={lotesPagoOn}
          />
        </section>
      )}

      {seleccionadas.size > 0 && (
        <div className="bulk-bar">
          <span>
            <b>{seleccionadas.size}</b> seleccionada(s)
          </span>
          <button onClick={() => marcarSeleccionadas(true)}>Habilitar para pagar</button>
          <button className="btn-ghost" onClick={() => marcarSeleccionadas(false)}>
            Quitar habilitación
          </button>
          {lotesPagoOn && esContaduria && (
            <>
              <button disabled={accionLoteEnCurso} onClick={crearLote}>
                Crear lote
              </button>
              {hayLotesEditables && (
                <select
                  className="select-agregar-lote"
                  aria-label="Agregar las seleccionadas a un lote"
                  value=""
                  disabled={accionLoteEnCurso}
                  onChange={(e) => {
                    if (e.target.value) agregarALote(Number(e.target.value));
                  }}
                >
                  <option value="">Agregar a lote ▾</option>
                  {destinosSeleccion.length === 0 && (
                    <option value="" disabled>
                      {`No hay lotes abiertos en ${monedaSeleccion ?? "esa moneda"}`}
                    </option>
                  )}
                  {destinosSeleccion.map((l) => (
                    <option key={l.id} value={String(l.id)}>
                      {l.nombre}
                    </option>
                  ))}
                </select>
              )}
            </>
          )}
          <button className="btn-ghost" onClick={() => setSeleccionadas(new Set())}>
            Cancelar
          </button>
        </div>
      )}

      <TablaToolbar
        busqueda={tabla.busqueda}
        onBuscar={tabla.setBusqueda}
        filtradas={paraTotales}
        totalSinFiltro={visibles.length}
        placeholder="Buscar por proveedor, codigo, comprobante o documento..."
      />

      <table className="compact">
        <thead>
          <tr>
            <th>
              <input
                type="checkbox"
                checked={filasPaginaSeleccionadas}
                title="Seleccionar todas las de esta pagina"
                onChange={(e) => {
                  const marcar = e.target.checked;
                  setSeleccionadas((prev) => {
                    const next = new Set(prev);
                    for (const f of tabla.filas) {
                      marcar ? next.add(claveDe(f)) : next.delete(claveDe(f));
                    }
                    return next;
                  });
                }}
              />
            </th>
            <th>Pagar</th>
            <ThOrdenable columna="proveedor_nombre" orden={tabla.orden} onOrdenar={tabla.alternarOrden}>
              Proveedor
            </ThOrdenable>
            <ThOrdenable columna="numero_comprobante" orden={tabla.orden} onOrdenar={tabla.alternarOrden}>
              Comprobante
            </ThOrdenable>
            <ThOrdenable columna="nro_documento" orden={tabla.orden} onOrdenar={tabla.alternarOrden}>
              Doc. SAP
            </ThOrdenable>
            <ThOrdenable columna="fecha_contabilizacion" orden={tabla.orden} onOrdenar={tabla.alternarOrden}>
              Contabiliz.
            </ThOrdenable>
            <ThOrdenable columna="fecha_vencimiento" orden={tabla.orden} onOrdenar={tabla.alternarOrden}>
              Vencimiento
            </ThOrdenable>
            <ThOrdenable columna="dias_vencido" orden={tabla.orden} onOrdenar={tabla.alternarOrden} alineado="derecha">
              Días
            </ThOrdenable>
            <ThOrdenable columna="saldo_vencido" orden={tabla.orden} onOrdenar={tabla.alternarOrden} alineado="derecha">
              Saldo vencido
            </ThOrdenable>
            <th>CFE</th>
            <th title={AYUDA_AUTORIZANTE}>Autorizó ingreso a SAP</th>
            {pagosBanco && <th title="Pago en los lotes del banco importados">Pago banco</th>}
            {lotesPagoOn && <th title="Lote de pago en el que está la factura">Lote</th>}
            <th></th>
          </tr>
        </thead>
        <tbody>
          {tabla.filas.length === 0 && (
            <tr>
              <td colSpan={COLUMNAS} className="empty-row">
                {items.length === 0
                  ? "No hay documentos cargados."
                  : "Ningun documento coincide con el filtro o la busqueda."}
              </td>
            </tr>
          )}
          {tabla.filas.map((item, indice) => {
            const clave = claveDe(item);
            // Encabezado de grupo cuando cambia el proveedor respecto de la
            // fila anterior de la MISMA página.
            const anterior = indice > 0 ? tabla.filas[indice - 1] : null;
            const abreGrupo = !anterior || anterior.card_code !== item.card_code;
            const subtotal = subtotales.get(item.card_code ?? "—") ?? [];

            return (
              <Fragment key={clave}>
                {abreGrupo && (
                  <tr className="grupo-row">
                    <td colSpan={COLUMNAS}>
                      <span className="grupo-nombre">{item.proveedor_nombre ?? "—"}</span>
                      <span className="grupo-codigo mono">{item.card_code ?? "—"}</span>
                      <span className="grupo-totales">
                        {subtotal.map(({ moneda, total }) => (
                          <span key={moneda} className="total-moneda">
                            {moneda} <b className="mono">{formatMonto(total)}</b>
                          </span>
                        ))}
                      </span>
                    </td>
                  </tr>
                )}
                <tr className={item.pago_habilitado ? "fila-habilitada" : undefined}>
                  <td>
                    <input
                      type="checkbox"
                      checked={seleccionadas.has(clave)}
                      onChange={(e) => toggleSeleccion(clave, e.target.checked)}
                    />
                  </td>
                  <td>
                    {/* El click se ve al instante; el POST viaja atrás. */}
                    <input
                      type="checkbox"
                      className="check-pagar"
                      checked={item.pago_habilitado}
                      disabled={guardando.has(clave)}
                      title="Habilitar esta factura para pagar"
                      onChange={(e) => marcar([item], e.target.checked)}
                    />
                  </td>
                  <td className="wrap-ok" title={item.proveedor_nombre ?? undefined}>
                    {item.proveedor_nombre ?? "—"}
                  </td>
                  <td className="mono wrap-ok">{item.numero_comprobante ?? "—"}</td>
                  <td className="mono">{item.nro_documento ?? "—"}</td>
                  <td className="mono">{formatFecha(item.fecha_contabilizacion)}</td>
                  <td className="mono">{formatFecha(item.fecha_vencimiento)}</td>
                  <td className="mono celda-monto">
                    {item.dias_vencido === null
                      ? "—"
                      : item.dias_vencido >= 0
                        ? item.dias_vencido
                        : `en ${-item.dias_vencido}`}
                  </td>
                  <td className="mono celda-monto">
                    {formatMonto(item.saldo_vencido)}
                    {item.saldo_vencido !== null && <span className="moneda">{item.moneda}</span>}
                  </td>
                  <td className="actions-cell">
                    {item.review_item_id ? (
                      <>
                        {item.pdf_blob_url && (
                          <button className="btn-ghost" onClick={() => abrirDocumento(item.review_item_id!, "pdf")}>
                            PDF
                          </button>
                        )}
                        <button className="btn-ghost" onClick={() => abrirDocumento(item.review_item_id!, "xml")}>
                          XML
                        </button>
                      </>
                    ) : (
                      <span className="sin-resolver" title={item.motivo_sin_cruce ?? undefined}>
                        —
                      </span>
                    )}
                  </td>
                  <td className="wrap-ok">
                    {(() => {
                      const a = autorizanteDeColumna(item);
                      return a.conDato ? (
                        <span title={`${AYUDA_AUTORIZANTE} ${formatFechaHoraUtc(a.fecha)}`}>{a.texto}</span>
                      ) : (
                        <span className="sin-resolver">{a.texto}</span>
                      );
                    })()}
                  </td>
                  {pagosBanco && (
                    <td className="wrap-ok">
                      {(() => {
                        const p = textoPagoBanco(item);
                        return (
                          <span className={`pago-banco pago-banco-${p.tono}`} title={p.ayuda}>
                            {p.texto}
                          </span>
                        );
                      })()}
                    </td>
                  )}
                  {lotesPagoOn && (
                    <td>
                      {item.lote_nombre ? (
                        <span
                          className={`badge ${item.lote_estado ? CLASE_ESTADO_LOTE[item.lote_estado] : "badge-wait"}`}
                          title={item.lote_estado ? `Estado del lote: ${ETIQUETA_ESTADO_LOTE[item.lote_estado]}` : undefined}
                        >
                          {item.lote_nombre}
                        </span>
                      ) : null}
                    </td>
                  )}
                  <td>
                    <LineasToggle expanded={expandidos.has(clave)} onToggle={() => toggleExpandido(clave)} />
                  </td>
                </tr>

                {expandidos.has(clave) && (
                  <>
                    <tr className="detalle-row">
                      <td colSpan={COLUMNAS}>
                        <div className="detalle-contexto">
                          <span>
                            <b>Origen:</b> {item.origen_proveedor ?? "—"}
                          </span>
                          {pagosBanco && item.pago_banco && (
                            <span>
                              <b>Pago banco:</b> lote {item.pago_banco.id_transaccion} del{" "}
                              {formatFecha(item.pago_banco.fecha)} ({item.pago_banco.estado_lote ?? "—"}) —{" "}
                              {descripcionCruce(item.pago_banco.estado_cruce).toLowerCase()}; línea del banco{" "}
                              {item.pago_banco.moneda} {formatMonto(item.pago_banco.importe_linea)}
                            </span>
                          )}
                          {item.marcado_por && (
                            <span>
                              <b>Habilitó:</b> {item.marcado_por} ({formatFecha(item.marcado_at)})
                            </span>
                          )}
                          {item.review_item_id ? (
                            <>
                              <span>
                                <b>CFE:</b> {tipoCfeLabel(item.cfe_tipo)}{" "}
                                {formatNumeroCfe(item.cfe_serie, item.cfe_nro)}
                              </span>
                              <span>
                                <b>RUT emisor:</b> {item.cfe_rut_emisor ?? "—"}
                              </span>
                              <span>
                                <b>Total del CFE:</b> {formatMonto(item.cfe_monto)}
                              </span>
                              {(["contaduria", "aprobador"] as const).map((paso) => {
                                const a = textoAutorizante(item, paso);
                                return (
                                  <span key={paso} title="Autorización del ingreso a SAP, no del pago">
                                    <b>{paso === "contaduria" ? "Envió (Contaduría):" : "Autorizó ingreso a SAP:"}</b>{" "}
                                    {a.texto}
                                    {a.fecha && ` (${formatFechaHoraUtc(a.fecha)})`}
                                  </span>
                                );
                              })}
                            </>
                          ) : (
                            <span className="rejection-reason">
                              <b>Sin CFE asociado:</b> {item.motivo_sin_cruce ?? "no se pudo determinar"}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                    {item.review_item_id && (
                      <LineasRow reviewItemId={item.review_item_id} colSpan={COLUMNAS} />
                    )}
                  </>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>

      <Paginador
        pagina={tabla.pagina}
        totalPaginas={tabla.totalPaginas}
        desde={tabla.desde}
        hasta={tabla.hasta}
        total={tabla.total}
        onIrAPagina={tabla.irAPagina}
      />
    </div>
  );
}
